import store from '@/store/index';
import config from '@/config/user.env';
import bigLogData from '@/utils/bigLogData';
import { getChildModuleNameByPath, getWebSystemByPath, statisticIfNeed } from '@/hooks/useMenuClick';
import { RouteLocationNormalizedGeneric, Router } from 'vue-router';

export function getCurrentRoleName(): string {
  const { curRole, roleList } = store.state;
  const roleItem = roleList.find((item: { roleCode: string }) => item.roleCode === curRole);
  return roleItem?.roleName ?? '';
}

export const resolveWebVersion = (webSystem: string, webVersion?: string) => {
  if (webVersion !== undefined && webVersion !== null) {
    return webVersion;
  }
  return webSystem === 'EL' ? config.webVersion || '' : '';
};

const logTrackError = (error: unknown, context: string) => {
  if (import.meta.env.DEV) {
    console.warn(`[bigDataTrack] ${context}`, error);
  }
};

/**
 * 基座路由统一页面埋点
 * - 须在 checkLogin 之后注册守卫（见 registerPageTrackGuard），保证 initSystem 完成、store 就绪后再上报
 * - 仅在 authority 就绪且匹配到 model_name1 时上报
 */
export function trackPageByRoute(to: RouteLocationNormalizedGeneric): void {
  const { userCode } = store.getters.user;
  const path = to.path?.split('?')[0] || '';
  if (!userCode || !statisticIfNeed(path)) {
    return;
  }

  // 与 OTP 一致：authority 未加载时不埋点
  const routerMap = store.getters.authority || [];
  if (!routerMap.length) {
    return;
  }

  const modelInfo = bigLogData.getModelInfoByRoute(to, { authorityOnly: true });
  if (!modelInfo?.model_name1) {
    return;
  }

  const childModuleName = getChildModuleNameByPath(path, getCurrentRoleName());
  const webSystem = getWebSystemByPath(path);
  const webVersion = resolveWebVersion(webSystem);
  const baseUrl = window.location.origin + window.location.pathname;
  const href = baseUrl && to.fullPath ? `${baseUrl}#${to.fullPath}` : window.location.href;

  try {
    bigLogData.sendDetailMessage({
      modelInfo,
      moduleName: 'C-EL',
      childModuleName,
      href,
      webSystem,
      webVersion,
    });
  } catch (error) {
    logTrackError(error, 'trackPageByRoute');
  }
}

/** 子应用 microApp.dispatch({ type: 'bigDataTrack', ... }) 按钮埋点统一入口 */
export function handleMicroBigDataTrack(
  data: {
    type?: string;
    modelInfo?: Record<string, string>;
    href?: string;
    childModuleName?: string;
    webSystem?: string;
    webVersion?: string;
  },
  routePath: string,
): boolean {
  if (data.type !== 'bigDataTrack' || !data.modelInfo?.model_name1) {
    return false;
  }
  const { userCode } = store.getters.user;
  if (!userCode) {
    return true;
  }
  const webSystem = data.webSystem || getWebSystemByPath(routePath);
  const childModuleName = data.childModuleName || getChildModuleNameByPath(routePath, getCurrentRoleName());
  try {
    bigLogData.sendDetailMessage({
      modelInfo: data.modelInfo,
      moduleName: 'C-EL',
      childModuleName,
      href: data.href || '',
      webSystem,
      webVersion: resolveWebVersion(webSystem, data.webVersion),
    });
  } catch (error) {
    logTrackError(error, 'handleMicroBigDataTrack');
  }
  return true;
}

/**
 * 注册页面埋点守卫（须在 main.ts 中 await checkLogin 之后调用）
 * 守卫顺序：registerRoutes → checkLogin/initSystem → 本守卫，单次导航只上报一次
 */
export function registerPageTrackGuard(router: Router): void {
  router.beforeEach((to, _from, next) => {
    trackPageByRoute(to);
    next();
  });
}
